/** @jsx jsx */
import { Fragment, h } from 'preact'
import { useState, useMemo, useEffect } from 'preact/hooks'
import { useRef } from 'react';

// Searchspring
import { Price, Results, useMediaQuery } from '@searchspring/snap-preact-components'
import { useTheme, CacheProvider } from '@searchspring/snap-preact-components/dist/cjs/providers'
import { defined, cloneWithProps } from '@searchspring/snap-preact-components/dist/cjs/utilities'
import { withController } from '@searchspring/snap-preact-components';


// Packages
import { jsx, css } from '@emotion/react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'

// Local 
import { useSwatches } from '../Helpers';
import { useSwatchesGlobal } from '../Helpers';
import { IconMore } from '../Icons/icon_more'
import { StarEmpty } from '../Icons/StarEmpty';
import { StarFull } from '../Icons/StarFull';
import { StarHalf } from '../Icons/StarHalf';

// CSS
import './result.css'

const CSS = {
    result: () =>
        css({
            '&.ss__result--grid': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                '& .ss__result__image-wrapper': {
                    flex: '1 0 auto',
                },
            },
            '&.ss__result--list': {
                display: 'flex',
                flexDirection: 'row',
                '& .ss__result__image-wrapper': {
                    flex: '0 0 33%',
                },
                '& .ss__result__details': {
                    flex: '1 1 auto',
                    textAlign: 'left',
                    marginLeft: '20px',
                    padding: 0,
                },
            },

            '& .ss__result__image-wrapper': {
                position: 'relative',
                '& .ss__result__badge': {
                    background: '#FFFFFF',
                },
            },

            '& .ss__result__details': {
                textAlign: 'center',

                '& .ss__result__details__title': {
                },
                '& .ss__result__details__pricing': {

                    '& .ss__result__price': {
                        fontSize: '1.2em',
                    },
                    '& .ss__price--strike': {
                        fontSize: '80%',
                    },
                },
                '& .ss__result__details__button': {
                    marginBottom: '10px',
                },
            },
        }),
}

export const Result = observer(properties => {
    const globalTheme = useTheme();
    const [swatchIndex, setSwatchIndex] = useState(0);
    const [swatchHoverImageIndex, setSwatchHoverImageIndex] = useState('null');

    const props = {
        layout: 'grid',
        ...globalTheme?.components?.result,
        ...properties,
        ...properties.theme?.components?.result,
    }

    const {
        result,
        hideBadge,
        hideSwatches,
        hidePricing,
        hideTitle,
        hideBrand,
        hidePromoMessage,
        hideFrameWidth,
        hideWishlist,
        hideReviews,
        detailSlot,
        fallback,
        disableStyles,
        className,
        layout,
        style,
        controller,
    } = props

    function fetchLocaleField(result, defaultField, spanishField) {
      const spanishData = result?.attributes?.[spanishField]?.length ? result?.attributes?.[spanishField] : null;
      const defaultData = result?.attributes?.[defaultField]?.length ? result?.attributes?.[defaultField] : null;
      return window.Shopify.locale === "es" && spanishData ? spanishData : defaultData ;
    }

    const core = result?.mappings?.core;
    const productID = core.uid;
    const badge = fetchLocaleField(result, "mfield_cql_badge", "mfield_spa_badge");
    const title = core.name;	
    const frameWidth = fetchLocaleField(result, "mfield_cql_frame_width", "mfield_spa_frame_width");
    const promoMessage = fetchLocaleField(result, "mfield_cql_pdp_promo_message", "mfield_spa_pdp_promo_message"); /* note look into or sync */
    const priceRange = result?.attributes?.ss_price_range ? result?.attributes?.ss_price_range : null;
    const variantColors = result?.attributes?.variant_frame_color
    const variantImages = result?.attributes?.variant_images_json
    //const testvariantImages = result?.attributes?.variant_images_json;
    //console.log(testvariantImages);
    const variantID = result?.attributes?.variant_id ? result?.attributes?.variant_id : productID;
    let variantIDArray = "";
    const swatchImages = result?.attributes.mfield_cql_swatches_json ? result?.attributes.mfield_cql_swatches_json : null;
    const reviewScore = result?.attributes.reviews_product_score;
    const reviewTotal = result?.attributes.reviews_total_reviews;
    let shouldSkip = false;
    let swatches = null;


    const [dynamicProductURL, setDynamicProductURL] = useState(core.url);

    if (swatchImages != null) {
        swatches = useSwatches({
            variantColors: variantColors,
            variantImages: variantImages,
            swatchImages: swatchImages,
            fallbackImage: core.imageUrl,
        });
    }
    else {
        swatches = useSwatchesGlobal({
            variantColors: variantColors,
            variantImages: variantImages,
            fallbackImage: core.imageUrl,
        });
    }
    
    const subProps = {
        price: {
            className: 'ss__result__price',
            ...globalTheme?.components?.price,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        badge: {
            className: 'ss__result__badge',
            content: badge,
            ...globalTheme?.components?.badge,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        image: {
            className: 'ss__result__image',
            alt: core?.name,
            src: core?.imageUrl,
            ...globalTheme?.components?.image,
            ...defined({
                disableStyles,
                fallback: fallback,
            }),
            theme: props.theme,
        },
        frameWidth: {
            className: 'ss__result__frame_width',
            content: frameWidth,
            ...globalTheme?.components?.frameWidth,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        promoMessage: {
            className: 'ss__result__promo_message',
            content: promoMessage,
            ...globalTheme?.components?.promoMessage,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
    }

    const styling = {}
    if (!disableStyles) {
        styling.css = [CSS.result(), style]
    } else if (style) {
        styling.css = [style]
    }

    //Handle variant url change on PLP swatch click
    useEffect(() => {
        //Grab prod handle and current swatch thats active
        const currentHandle = result?.attributes?.handle;
        const currentColor = [...swatches][swatchIndex]?.frame_color;
        //const currentImage = [...swatches][swatchIndex]?.img;
        const numberOfVariants = JSON.parse(result.attributes.variants);

        if(numberOfVariants.length > 1 && currentColor) {
            //JSON api "call"
            fetch(`https://eyemart-express.myshopify.com/products/${currentHandle}.js`)
            .then(response => response.json())
            .then(product => {
                product.variants.forEach((variant) => {
                    //Find variant ID to append
                    if(variant.title == currentColor) {
                        const variantURLConcat = `${core.url}?variant=${variant.id}`;
                        setDynamicProductURL(variantURLConcat);

                        //Update wishlist icon attribute
                        const currentProduct = document.querySelector(`.ss__result-link[aria-label='${core.name}']`).parentElement;
                        const wishlistElement = currentProduct.querySelector("wishlist-button-collection");

                        if(wishlistElement) {
                            wishlistElement.setAttribute("data-variant-id", variant.id);
                        }
                    }
                });
            });
        }
        else {
            setDynamicProductURL(core.url);
        }
    }, [swatches, swatchIndex]);

    const displayPrices = (priceRange) => {

        let decimalPlaces = 0;
        let corePrice = String(core.price);
        let msrpPrice = String(core.msrp);

        if (corePrice.includes('.') || msrpPrice.includes('.')) {
            decimalPlaces = 2;
        }

        if (priceRange) {
            let priceRange1 = String(priceRange[1]);
            let priceRange2 = String(priceRange[0]);

            if (priceRange1.includes('.') || priceRange2.includes('.')) {
                decimalPlaces = 2;
            }

            return (
                <>
                    <Price {...subProps.price} value={Number(priceRange[1])} decimalPlaces={decimalPlaces} />
                    <span class="em-dash">&#8211;</span>
                    <Price {...subProps.price} value={Number(priceRange[0])} decimalPlaces={decimalPlaces} />
                </>
            )
        }

        //otherwise, return core price
        return (
            <>
               {core.price < core.msrp ? (
                    <>
                        <Price {...subProps.price} value={core.msrp} lineThrough={true} decimalPlaces={decimalPlaces} />
                        &nbsp;
                        <Price {...subProps.price} value={core.price} decimalPlaces={decimalPlaces} />
                    </>
                ) : (
                    <Price {...subProps.price} value={core.price} decimalPlaces={decimalPlaces} />
                )}
            </>
        )
    }

    //Fix for default selected variant being undefined
    let firstVariantOnLoad = "";

    if (variantID) {
        firstVariantOnLoad = variantID.split("|")[0];
    }

    const [selectedSwatch, setSelectedSwatch] = useState(0);
    const [selectedURL, setSelectedURL] = useState(firstVariantOnLoad);
    const [imageChange, setImageChange] = useState();


    const DynamicVariants = withController(
        observer(({ controller, result }) => {

            const urlParams = new URLSearchParams(window.location.search);

            if(!urlParams.get('q') && imageChange != true) {
                if(JSON.parse(variantImages)[0]) {
                    core.imageUrl = JSON.parse(variantImages)[0].img;
                    setImageChange(true);
                }
            }

            const store = controller.store;
            const variantsConfig = store.custom.variantsConfig;
            const { attributes } = result;
            const intellisuggest = (e) => controller.track.product.click(e, result);


            const handleSelectedClass = (e, index) => {
                let swatchIndex = e.target.getAttribute('swatchIndex');
                if(swatchIndex == index) {
                    setSelectedSwatch(swatchIndex);
                }
            };

            const handleSelectedURL = () => {
                const { custom } = result;
                const variantURL = custom.variantSelected.url;
                let variantID = variantURL.slice(-14)
                setSelectedURL(variantID);
            }

            const swatchList = [...swatches];
    
            return variantsConfig.swap && attributes[variantsConfig.field] && attributes[variantsConfig.field].length !== 0 ? (
                <>
                    <div className="all-finishes"> 
                        
                        {attributes[variantsConfig.field].slice(0, variantsConfig.limit).map((swatch, i) =>
                            i <= 3 ? (
                                <div id={swatch.id} className="finish-parent">
                                    <div
                                        className={selectedSwatch == i ? "finish selectedSwatch" : "finish" }
                                        onClick={(e) => {
                                            variantsConfig.swap(result, swatch);
                                            intellisuggest(e);
                                            handleSelectedClass(e, i);
                                            handleSelectedURL();
                                        }}
                                        title={swatch?.colourfamily}
                                    >
                                            {swatchList.filter(swatchImg => swatch.frame_color === swatchImg.frame_color)
                                            .map((swatchImg) => (
                                                <img
                                                    src={swatchImg.swatch_image}
                                                    alt={swatch.frame_color}
                                                    width={25}
                                                    height={25}
                                                    loading="lazy"
                                                    swatchIndex={i}
                                                />
                                            ))}

                                    </div>
                                </div>
                            ) : null
                        )}
    
                        {attributes[variantsConfig.field].length > 4 ? (
                            <span className="more-finishes-ava">+{attributes[variantsConfig.field].length - 4}</span>
                        ) : null}
                    </div>
                </>
            ) : null;
        }));
    
    const regex = /.png/i;
    const minifiedString = '_600x600.png';
    
    // Original Product Image
    const productImage = [...swatches][swatchIndex]?.thumbnail ? [...swatches][swatchIndex]?.thumbnail : core.imageUrl;
    /// Check if original is minified, if not, replace it with minified version
    let minifiedProductImage = productImage.includes(minifiedString) ? productImage : productImage.replace(regex, minifiedString);
    // Check if minified version exist, if not replace it with original
    minifiedProductImage = minifiedProductImage && minifiedProductImage.length > 1 ? minifiedProductImage : productImage;

    // Original Hover Product Image
    const productHoverImage = [...swatches][swatchHoverImageIndex]?.thumbnail ? [...swatches][swatchHoverImageIndex]?.thumbnail : core.imageUrl;
    /// Check if original is minified, if not, replace it with minified version
    let minifiedHoverProductImage = productHoverImage.includes(minifiedString) ? productHoverImage : productHoverImage.replace(regex, minifiedString);
    // Check if minified version exist, if not replace it with original
    minifiedHoverProductImage = minifiedHoverProductImage && minifiedHoverProductImage.length > 1 ? minifiedHoverProductImage : productHoverImage;

    

    return (
        <CacheProvider>
            <article
                {...styling}
                className={classnames('ss__result', `ss__result--${layout}`, className)}
            >
                <a
                    href={dynamicProductURL}
                    onMouseDown={e => {
                        controller?.track?.product?.click(e, result)
                    }}
                    className="ss__result-link"
                    aria-label={core.name}
                >
                    <div className="product-card-top-wrapper">
                        <div className="ss__result__image-wrapper">
                            {!hideBadge && (
                                <div className="ss__badge ss__result__badge">
                                    {badge}
                                </div>
                            )}
                            <div
                                {...styling}
                                className={classnames('ss__image', 'ss__result__image')}
                            >

                            {(swatchHoverImageIndex === 'null') && (
                                <img
                                    id={`ProductImage-${result.id}`}
                                    alt={core.name}
                                    class="product__img product__img--main lazyload"
                                    data-aspectratio="1.4967637540453074"
                                    data-sizes="auto"
                                    data-image
                                    src={minifiedProductImage}
                                />
                            )}

                            {(swatchHoverImageIndex !== 'null') && (
                                <img
                                    id={`ProductImage-${result.id}`}
                                    alt={core.name}
                                    class="product__img product__img--main lazyload"
                                    data-aspectratio="1.4967637540453074"
                                    data-sizes="auto"
                                    data-image
                                    src={minifiedHoverProductImage}
                                />
                            )}

                            </div>
                        </div>
                        <div className="ss__result__details">
                            {!hideBrand && (
                                <div
                                    className="ss__result__details__title"
                                    dangerouslySetInnerHTML={{
                                        __html: core.brand,
                                    }}
                                />
                            )}
                            {!hideTitle && (
                            <div className="ss__result__details__title recommendations_carousel_title"
                                  dangerouslySetInnerHTML={{
                                        __html: core.name,
                                    }}
                            />
                            )}
                            {!hidePricing && (
                                <div className="ss__result__details__pricing">
                                    {displayPrices(priceRange)}
                                </div>
                            )}
                            {cloneWithProps(detailSlot, { result })}
                        </div>
                        <div className="ss__result__details">
                        {!hideTitle && (
                            <span className="ss__result__details__product_title"
                                  dangerouslySetInnerHTML={{
                                        __html: core.name,
                                    }}
                            />
                        )}
                        {frameWidth !== '' && title !== '' && (
                            <span className="ss__result__details__bracket">| 
                            </span>
                        )}
                        {!hideFrameWidth && (
                            <span className="ss__result__details__frame_width">
                                {frameWidth}
                            </span>
                        )}
                        </div>
                        {!hidePromoMessage && (
                              <div className="ss__result__details__promo_message">
                                <p className="product__promo_message-text">
                                    {promoMessage}
                                </p>
                            </div>
                        )}
                    </div>
                    </a>
                    <div className="product-card-bottom-wrapper">
                        {!hideSwatches && swatches.size > 0 ? (
                            // We can't call <DynamicVariants> without ss_swatches field
                            // Where this field comes from or how it's populated, cant seem to tell. It's on Propel store but not here.
                            // Use <ColorSwatches> component for now

                            // <div>
                            //     <DynamicVariants
                            //         result={result}
                            //         // Do not pass {controller} here
                            //     />
                            // </div>

                            <ColorSwatches
                                swatches={swatches}
                                setSwatchIndex={setSwatchIndex}
                                swatchIndex={swatchIndex}
                                staticProductURL={core.url}
                                setproductName={core.name}
                                setSwatchHoverImageIndex={setSwatchHoverImageIndex}
                            />
                        ) : null}
                    </div>
            </article>
        </CacheProvider>
    )
})

const ColorSwatches = ({
    swatches, 
    setSwatchIndex, 
    swatchIndex, 
    staticProductURL, 
    setproductName,
    setSwatchHoverImageIndex
}) => {

    let breakNumber = 4; 
    const productName = setproductName;
    const productURL = staticProductURL;
    const swatchList = [...swatches];

    return (
        <div className="ss__result__swatches">
            {swatchList.map((swatch, index) => {
                let screenSize = window.matchMedia("(max-width: 740px)");
                function mobileQuery(screenSize) {
                    if (screenSize.matches) { // If media query matches
                        breakNumber = 3; 
                    } else {    
                        breakNumber = 4;
                    }
                }
                screenSize.addEventListener("change", function() {
                    mobileQuery(screenSize);
                });

                if (index > breakNumber) return null
                return (
                    <>
                        {index === breakNumber ? (
                            <a className="link-icon-more" href={productURL} title={productName}><IconMore
                                count = {swatchList.length}
                                breakpoint = {breakNumber}
                            /></a>
                        ) : (
                            <div
                                className={classnames('swatch-image', {
                                    'swatch-image-selected': swatchIndex === index,
                                })}
                                title={swatch.frame_color}
                                data-index={index}
                                data-thumbnail={swatch.thumbnail}
                            >

                            {swatch.swatch_image ? (
                                <img
                                    src={swatch.swatch_image}
                                    onClick={e => {
                                        e.preventDefault();
                                        setSwatchIndex(index)
                                    }}
                                    onMouseEnter={e => {
                                        e.preventDefault();

                                        if (index !== swatchIndex) {
                                            setSwatchHoverImageIndex(index)
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        e.preventDefault();
                                        setSwatchHoverImageIndex('null')
                                    }}
                                    alt={swatch.frame_color}
                                    width={25}
                                    height={25}
                                    loading="lazy"
                                />
                            ) : null}

                            </div>
                        )}
                    </>
                )
            })}
        </div>
    )
}
